<template>
  <div class="profile-container">
    <div v-if="loading" class="loading">加载中...</div>
    <div v-else-if="userProfile" class="profile-content">
      <div class="profile-header">
        <el-avatar :size="100" :src="userProfile.avatar || 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'" class="profile-avatar" />
        <div class="user-basic-info">
          <h2 class="username">{{ userProfile.name || userProfile.username || '未知用户' }}</h2>
          <p class="user-signature">{{ userProfile.signature || '暂无签名' }}</p>
        </div>
      </div>

      <div class="profile-details">
        <div class="detail-section">
          <h3 class="section-title">账户信息</h3>
          <div class="detail-item">
            <label>用户账号:</label>
            <span>{{ userProfile.username || '未设置' }}</span>
          </div>
          <div class="detail-item">
            <label>用户邮箱:</label>
            <span>{{ userProfile.email || '未填写' }}</span>
          </div>
          <div class="detail-item">
            <label>联系电话:</label>
            <span>{{ userProfile.phone || '未填写' }}</span>
          </div>
          <div class="detail-item">
            <label>注册时间:</label>
            <span>{{ formatDate(userProfile.createTime) }}</span>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">统计信息</h3>
          <div class="stats-grid">
            <div class="stat-item" @click="showFollowingList">
              <div class="stat-number">{{ userProfile.followeeCount || 0 }}</div>
              <div class="stat-label">关注数</div>
            </div>
            <div class="stat-item" @click="showFollowerList">
              <div class="stat-number">{{ userProfile.followerCount || 0 }}</div>
              <div class="stat-label">粉丝数</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ userProfile.postCount || 0 }}</div>
              <div class="stat-label">作品数</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">{{ userProfile.likeCount || 0 }}</div>
              <div class="stat-label">获赞数</div>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">账户状态</h3>
          <div class="detail-item">
            <label>用户角色:</label>
            <span>{{ userProfile.roleName || '普通用户' }}</span>
          </div>
          <div class="detail-item">
            <label>账户状态:</label>
            <span :class="statusClass">{{ statusText }}</span>
          </div>
        </div>
      </div>

      <div class="profile-actions">
        <el-button type="primary" round @click="showEditProfileModal = true">编辑资料</el-button>
        <el-button type="warning" round @click="showChangePasswordDialog">修改密码</el-button>
      </div>
    </div>
    <div v-else class="error">无法加载用户资料</div>

    <!-- 修改密码对话框 -->
    <el-dialog
      v-model="showPasswordDialog"
      title="修改密码"
      width="400px"
      :close-on-click-modal="false"
      :before-close="() => { showPasswordDialog = false; }"
    >
      <el-form :model="passwordForm" label-width="100px" style="padding: 10px 0;">
        <el-form-item label="原密码">
          <el-input 
            v-model="passwordForm.oldPassword" 
            type="password" 
            placeholder="请输入原密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input 
            v-model="passwordForm.newPassword" 
            type="password" 
            placeholder="请输入新密码"
            show-password
          />
        </el-form-item>
        <el-form-item label="确认新密码">
          <el-input 
            v-model="passwordForm.confirmPassword" 
            type="password" 
            placeholder="请再次输入新密码"
            show-password
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showPasswordDialog = false">取消</el-button>
          <el-button type="primary" @click="confirmChangePassword">确认修改</el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- 编辑个人资料对话框 -->
    <EditProfileModal
      v-model="showEditProfileModal"
      @updated="handleProfileUpdated"
      @close="showEditProfileModal = false"
    />
    
    <!-- 关注列表对话框 -->
    <FollowingListModal
      v-model="showFollowingListModal"
      @close="showFollowingListModal = false"
      @report-user="handleReportEvent"
    />
    
    <!-- 粉丝列表对话框 -->
    <FollowerListModal
      v-model="showFollowerListModal"
      @close="showFollowerListModal = false"
      @report-user="handleReportEvent"
    />
    
    <!-- 其他用户详情对话框 -->
    <OtherUserProfileModal
      v-model="showOtherUserProfileModal"
      :userId="selectedUserId"
      @close="showOtherUserProfileModal = false"
    />
    
    <!-- 举报对话框 -->
    <ReportModal
      ref="reportModalRef"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getUserProfile, changePassword } from '@/user/api/userApi';
import { formatDate } from '@/utils/date';
import EditProfileModal from './EditProfileModal.vue'; // 导入编辑资料弹窗组件
import FollowingListModal from '../interaction/FollowingListModal.vue'; // 导入关注列表弹窗组件
import FollowerListModal from '../interaction/FollowerListModal.vue'; // 导入粉丝列表弹窗组件
import OtherUserProfileModal from './OtherUserProfileModal.vue'; // 导入其他用户资料弹窗组件
import ReportModal from '../report/ReportModal.vue'; // 导入举报弹窗组件

// --- API响应接口定义 ---
interface UserProfile {
  id: number;
  username: string;
  name: string;
  avatar: string;
  email?: string;
  phone?: string;
  signature?: string;
  status: number;
  roleName: string;
  createTime: string;
  likeCount: number;
  postCount: number;
  followerCount: number;
  followeeCount: number;
}

interface ApiResponse {
  code: number;
  msg: string | null;
  data: UserProfile;
}

// 修改密码表单数据
interface ChangePasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const userProfile = ref<UserProfile | null>(null);
const loading = ref(true);
const router = useRouter();
const showPasswordDialog = ref(false);
const showEditProfileModal = ref(false);
const showFollowingListModal = ref(false); // 控制关注列表弹窗显示
const showFollowerListModal = ref(false); // 控制粉丝列表弹窗显示
const showOtherUserProfileModal = ref(false); // 控制其他用户资料弹窗显示
const selectedUserId = ref<number | null>(null); // 选中的用户ID
const reportModalRef = ref(); // 添加举报弹窗引用
const passwordForm = ref<ChangePasswordForm>({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
});

// 状态文本和样式（不再是计算属性，而是普通ref，在加载数据时更新）
const statusText = ref('');
const statusClass = ref('');

// 更新状态相关信息
const updateStatusInfo = (status: number) => {
  switch (status) {
    case 0:
      statusText.value = '封禁';
      statusClass.value = 'status-banned';
      break;
    case 1:
      statusText.value = '正常';
      statusClass.value = 'status-normal';
      break;
    case 2:
      statusText.value = '禁言';
      statusClass.value = 'status-muted';
      break;
    default:
      statusText.value = '未知';
      statusClass.value = 'status-unknown';
  }
};

const loadUserProfile = async () => {
  try {
    loading.value = true;
    
    // 调用真实的API获取用户资料
    const response = await getUserProfile();
    
    if (response.data && response.data.code === 1) {
      userProfile.value = response.data.data;
      // 更新状态信息
      updateStatusInfo(response.data.data.status);
    } else {
      throw new Error(response.data.msg || '获取用户资料失败');
    }
  } catch (error: any) {
    console.error('获取用户资料失败:', error);
    ElMessage.error(error.message || '获取用户资料失败');
  } finally {
    loading.value = false;
  }
};

// 显示修改密码对话框
const showChangePasswordDialog = () => {
  showPasswordDialog.value = true;
  // 重置表单
  passwordForm.value = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  };
};

// 确认修改密码
const confirmChangePassword = async () => {
  if (!passwordForm.value.oldPassword) {
    ElMessage.error('请输入原密码');
    return;
  }
  
  if (!passwordForm.value.newPassword) {
    ElMessage.error('请输入新密码');
    return;
  }
  
  if (passwordForm.value.newPassword.length < 6) {
    ElMessage.error('新密码长度至少为6位');
    return;
  }
  
  if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
    ElMessage.error('两次输入的新密码不一致');
    return;
  }
  
  try {
    const response = await changePassword(
      passwordForm.value.oldPassword, 
      passwordForm.value.newPassword
    );
    
    if (response.data.code === 1) {
      ElMessage.success('密码修改成功');
      showPasswordDialog.value = false;
      // 清空表单
      passwordForm.value = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
    } else {
      ElMessage.error(response.data.msg || '密码修改失败');
    }
  } catch (error) {
    ElMessage.error('密码修改失败，请稍后重试');
    console.error('修改密码失败:', error);
  }
};

// 处理资料更新后的回调
const handleProfileUpdated = (updatedProfile: any) => {
  if (userProfile.value) {
    // 更新当前用户资料
    userProfile.value.name = updatedProfile.name;
    userProfile.value.email = updatedProfile.email;
    userProfile.value.phone = updatedProfile.phone;
    userProfile.value.signature = updatedProfile.signature;
    
    ElMessage.success('个人资料已更新');
  }
};

// 显示关注列表
const showFollowingList = () => {
  showFollowingListModal.value = true;
};

// 显示粉丝列表
const showFollowerList = () => {
  showFollowerListModal.value = true;
};

// 跳转到我的作品页面
const goToMyPosts = () => {
  router.push('/user/posts');
};

// 处理举报事件（来自子评论或用户）
const handleReportEvent = (payload: { targetType: number, targetId: number }) => {
  reportModalRef.value?.openReportDialog(payload.targetType, payload.targetId);
};

// 处理打开用户资料模态框事件
const handleOpenUserProfileModal = (event: Event) => {
  const detail = (event as CustomEvent).detail;
  if (detail && detail.userId) {
    selectedUserId.value = detail.userId;
    showOtherUserProfileModal.value = true;
  }
};

onMounted(() => {
  loadUserProfile();
  // 添加事件监听器
  window.addEventListener('openUserProfileModal', handleOpenUserProfileModal);
});

onUnmounted(() => {
  // 移除事件监听器
  window.removeEventListener('openUserProfileModal', handleOpenUserProfileModal);
});
</script>

<style scoped>
/* 导入手写风格字体 */
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&family=Quicksand:wght@500;600&family=Open+Sans:wght@400;600&display=swap');

.profile-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: linear-gradient(135deg, #fefefe 0%, #f8f3eb 100%);
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(139, 125, 107, 0.15);
  position: relative;
  overflow: hidden;
  font-family: 'Open Sans', 'Nunito', 'Quicksand', 'Microsoft YaHei', sans-serif;
}

/* 背景编织纹理 */
.profile-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    radial-gradient(#d4c19c 1px, transparent 1px),
    radial-gradient(#d4c19c 1px, transparent 1px);
  background-size: 40px 40px;
  background-position: 0 0, 20px 20px;
  opacity: 0.08;
  z-index: 0;
  pointer-events: none;
}

.profile-content {
  position: relative;
  z-index: 1;
}

.loading, .error {
  text-align: center;
  padding: 40px;
  font-size: 16px;
  color: #8B7D6B;
  font-weight: 500;
}

.profile-header {
  display: flex;
  align-items: center;
  padding: 30px 0;
  border-bottom: 1px solid #e9dcc8;
  position: relative;
}

.profile-avatar {
  margin-right: 25px;
  border: 3px solid #f0e6d2;
  box-shadow: 0 4px 12px rgba(139, 125, 107, 0.15);
}

.user-basic-info {
  flex: 1;
}

.user-basic-info h2 {
  margin: 0 0 12px;
  color: #5c5142;
  font-size: 26px;
  font-weight: 600;
  font-family: 'Quicksand', 'Nunito', 'Microsoft YaHei', sans-serif;
}

.user-signature {
  color: #8B7D6B;
  font-size: 16px;
  font-style: italic;
  margin: 0;
  font-family: 'Nunito', 'Open Sans', 'Microsoft YaHei', sans-serif;
}

.profile-details {
  padding: 20px 0;
}

.detail-section {
  margin-bottom: 35px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(139, 125, 107, 0.08);
  border: 1px solid #f0e6d2;
}

.section-title {
  color: #8B7D6B;
  border-left: 4px solid #d48341;
  padding-left: 15px;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: 600;
  font-family: 'Quicksand', 'Nunito', 'Microsoft YaHei', sans-serif;
}

.detail-item {
  display: flex;
  margin-bottom: 15px;
  padding: 10px 0;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.detail-item label {
  width: 120px;
  color: #6b5e4d;
  font-weight: 600;
  font-size: 15px;
}

.detail-item span {
  flex: 1;
  color: #5c5142;
  font-size: 15px;
  padding-left: 10px;
}

.detail-item:hover {
  background: rgba(249, 242, 235, 0.5);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.stat-item {
  text-align: center;
  padding: 20px 10px;
  background: linear-gradient(135deg, #f9f2eb 0%, #fefefe 100%);
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(139, 125, 107, 0.1);
  border: 1px solid #e9dcc8;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.stat-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(139, 125, 107, 0.15);
}

.stat-number {
  font-size: 26px;
  font-weight: bold;
  color: #d48341;
  display: block;
  font-family: 'Nunito', 'Quicksand', 'Microsoft YaHei', sans-serif;
}

.stat-label {
  font-size: 14px;
  color: #8B7D6B;
  margin-top: 5px;
}

.status-normal {
  color: #67c23a;
}

.status-banned {
  color: #f56c6c;
}

.status-muted {
  color: #e6a23c;
}

.profile-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  padding: 25px 0;
  margin-top: 20px;
}

.profile-actions .el-button {
  border-radius: 25px;
  padding: 12px 28px;
  font-weight: 600;
  box-shadow: 0 4px 10px rgba(212, 131, 65, 0.2);
  transition: all 0.3s ease;
}

.profile-actions .el-button--primary {
  background: linear-gradient(45deg, #d48341, #e6ab6a);
  border: none;
}

.profile-actions .el-button--info {
  background: linear-gradient(45deg, #8B7D6B, #a89c8b);
  border: none;
}

.profile-actions .el-button--danger {
  background: linear-gradient(45deg, #f56c6c, #f78989);
  border: none;
}

.profile-actions .el-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(212, 131, 65, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .profile-container {
    padding: 15px;
    margin: 10px;
  }
  
  .profile-header {
    flex-direction: column;
    text-align: center;
  }
  
  .profile-avatar {
    margin-right: 0;
    margin-bottom: 20px;
  }
  
  .user-basic-info h2 {
    font-size: 22px;
  }
  
  .detail-item {
    flex-direction: column;
  }
  
  .detail-item label {
    width: 100%;
    margin-bottom: 5px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .profile-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .profile-actions .el-button {
    width: 80%;
  }
}
</style>